import React from 'react';
import { Button, Modal, Popover, Row, Space } from 'antd';
import moment from 'moment';
import styled from 'styled-components';
import { CommunicationsHistory } from 'types';

import { useIntl } from 'common/config/i18n';
import { htmlParser } from 'common/utils/html-parser';
import { OdsSummaryContent } from '../target-customer-stations/utils';
import { useTheme } from 'common/styling/theme';
import { InfoCircleFilled } from '@ant-design/icons';

interface Props {
  data: CommunicationsHistory;
  isOpen: boolean;
  onClose: () => void;
}

const StyledModal = styled(Modal)`
  .ant-modal-title {
    color: ${({ theme }) => theme.colors.secondary.main};
    font-size: ${({ theme }) => theme.font.size.h3};
  }
`;
const Label = styled.label<{ isClickable?: boolean }>`
  font-size: ${({ theme }) => theme.font.size.body};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  ${({ isClickable }) => isClickable && 'cursor: pointer'}
`;
const Value = styled.label`
  font-size: ${({ theme }) => theme.font.size.body};
`;
const EmptySpace = styled.div`
  height: ${({ theme }) => theme.spacing.small};
`;
export const InfoIconButton = styled(Button)`
  background: none;
  border: none;
  box-shadow: none;
  padding: 0;
  :hover {
    color: black !important;
  }
`;

const CommunicationHistoryModal: React.FC<Props> = ({ isOpen, onClose, data }) => {
  const { t } = useIntl();
  const { theme } = useTheme();
  const title =
    t('popin', 'POPIN_SERVICE_NUMBER') +
    data.serviceName +
    ' ' +
    t('common', 'of') +
    ' ' +
    moment(data.serviceDate).format('DD/MM') +
    ' : ' +
    data.processName +
    ' - ' +
    data.situationName;

  return (
    <StyledModal title={title} open={isOpen} closable footer={null} onCancel={onClose} width={'70rem'}>
      <Space direction="vertical">
        <EmptySpace />
        {data.communicationSchedule && (
          <div>
            <Label>{t('popin', 'POPIN_DETAILS_SCHEDULED_DATE_TIME')}</Label> :{' '}
            <Value>{moment(data.communicationSchedule).format('DD/MM/YYYY HH:mm')}</Value>
          </div>
        )}
        {data.creationDate && (
          <div>
            <Label>{t('popin', 'POPIN_DETAILS_CREATION_DATE_TIME')}</Label> :{' '}
            <Value>{moment(data.creationDate).format('DD/MM/YYYY HH:mm')}</Value>
          </div>
        )}
        {data.sentDateTime && (
          <div>
            <Label>{t('popin', 'POPIN_DETAILS_SEND_DATE_TIME')}</Label> :{' '}
            <Value>{moment(data.sentDateTime).format('DD/MM/YYYY HH:mm')}</Value>
          </div>
        )}
        {data.sender && (
          <div>
            <Label>{t('popin', 'POPIN_TRANSMITTER')}</Label>: <Value>{data.sender}</Value>
          </div>
        )}
        {data.author && (
          <div>
            <Label>{t('popin', 'POPIN_DETAILS_SIGNATURE')}</Label>: <Value>{data.author}</Value>
          </div>
        )}
        {data.communicationChannels && data.communicationChannels.length > 0 && (
          <div>
            <Label>{t('popin', 'POPIN_CHANNEL')}</Label>: <Value>{data.communicationChannels.join(', ')}</Value>
          </div>
        )}
        {data.communicationObject && (
          <div>
            <Label>{t('popin', 'POPIN_MAIL_OBJECT')}</Label>: <Value>{data.communicationObject}</Value>
          </div>
        )}
        {data.communicationHeader && (
          <div>
            <Label>{t('popin', 'POPIN_HEADER_TITLE')}</Label>: <Value>{data.communicationHeader}</Value>
          </div>
        )}
        <EmptySpace />
        {data.customerSelection && (
          <div>
            <Label>{t('popin', 'POPIN_CLIENTS_TYPE')}</Label>: <Value>{data.customerSelection}</Value>
          </div>
        )}
        {!!data.specificBookings && (
          <div>
            <Label>{t('popin', 'POPIN_DV_NUMBERS')}</Label>: <Value>{data.specificBookings}</Value>
          </div>
        )}
        {data.impactedStations && data.impactedStations.length > 0 && (
          <div>
            <Label>{t('popin', 'POPIN_IMPACTED_STATIONS')}</Label>:{' '}
            <Value>{data.impactedStations.map((station) => station.shortCode).join(', ')}</Value>
          </div>
        )}
        {data.excludedStations && data.excludedStations.length > 0 && (
          <div>
            <Label>{t('popin', 'POPIN_EXCLUDED_STATIONS')}</Label>:{' '}
            <Value>{data.excludedStations.map((station) => station.shortCode).join(', ')}</Value>
          </div>
        )}
        {data.overviewOD && data.overviewOD.length > 0 && (
          <div>
            <Popover
              placement={data.overviewOD.length > 10 ? 'bottom' : 'bottomRight'}
              color={theme.colors.common.black}
              content={
                <OdsSummaryContent
                  noHeader
                  color={theme.colors.common.white}
                  content={data.overviewOD.map((item) => item.split(' ; ')) || []}
                />
              }
              trigger="click"
            >
              <InfoIconButton>
                <Row style={{ display: 'flex', alignItems: 'center' }}>
                  <Label isClickable>{t('popin', 'POPIN_DETAILS_ODS_SUMMARY_LABEL')}</Label>
                  <div style={{ fontSize: '22px' }}>
                    <InfoCircleFilled style={{ marginLeft: 5 }} />
                  </div>
                </Row>
              </InfoIconButton>
            </Popover>
          </div>
        )}
        {data.selectedCarriages && (
          <div>
            <Label>{t('popin', 'SELECTED_COACHES')}</Label>: <Value>{data.selectedCarriages}</Value>
          </div>
        )}
        {data.other && (
          <div>
            <Label>{t('popin', 'POPIN_OTHERS')}</Label>: <Value>{data.other}</Value>
          </div>
        )}
        {(!!data.concernedPassengers || data.concernedPassengers === 0) && (
          <div>
            <Label>{t('popin', 'CONCERNED_PASSENGERS')}</Label> <Value>{data.concernedPassengers}</Value>
          </div>
        )}
        <EmptySpace />
        {data.motif && (
          <div>
            <Label>{t('popin', 'POPIN_DETAILS_REASON')}</Label>: <Value>{data.motif}</Value>
          </div>
        )}
        {data.delay && (
          <div>
            <Label>{t('popin', 'POPIN_DETAILS_DELAY')}</Label>: <Value>{data.delay}</Value>
          </div>
        )}
        {data.stationReplacement && (
          <div>
            <Label>{t('popin', 'POPIN_DETAILS_SUBSTITUTION_STATION')}</Label>: <Value>{data.stationReplacement}</Value>
          </div>
        )}
        {data.newSchedule && (
          <div>
            <Label>{t('popin', 'POPIN_DETAILS_NEW_SCHEDULE')}</Label>: <Value>{data.newSchedule}</Value>
          </div>
        )}
        {data.dock && (
          <div>
            <Label>{t('popin', 'POPIN_DETAILS_PLATFORM')}</Label>: <Value>{data.dock}</Value>
          </div>
        )}
        {data.principalZoneLongMessage && (
          <div>
            <Label>{t('popin', 'POPIN_DETAILS_INFORMATION_COM')}</Label>:{' '}
            <Value>{htmlParser(data.principalZoneLongMessage)}</Value>
          </div>
        )}
        {data.principalZoneShortMessage && (
          <div>
            <Label>{t('popin', 'POPIN_DETAILS_INFORMATION_COM_SHORT')}</Label>:{' '}
            <div style={{ whiteSpace: 'pre-wrap' }}>
              <Value>{data.principalZoneShortMessage}</Value>
            </div>
          </div>
        )}
        {data.secondaryZoneLongMessage && (
          <div>
            <Label>{t('popin', 'POPIN_DETAILS_OTHER_INFORMATION_COM')}</Label>:{' '}
            <Value>{htmlParser(data.secondaryZoneLongMessage)}</Value>
          </div>
        )}
        {data.secondaryZoneShortMessage && (
          <div>
            <Label>{t('popin', 'POPIN_DETAILS_OTHER_INFORMATION_COM_SHORT')}</Label>:{' '}
            <Value>{data.secondaryZoneShortMessage}</Value>
          </div>
        )}
        {data.compensationLongMessage && (
          <div>
            <Label>{t('popin', 'POPIN_DETAILS_SAV')}</Label>: <Value>{htmlParser(data.compensationLongMessage)}</Value>
          </div>
        )}
        {data.compensation && (
          <div>
            <Label>{t('popin', 'POPIN_DETAILS_COMPENSATION')}</Label>: <Value>{data.compensation}</Value>
          </div>
        )}
      </Space>
    </StyledModal>
  );
};
export default CommunicationHistoryModal;
